import React, { createRef, type SyntheticEvent, type MouseEvent, type KeyboardEvent } from 'react';
import { SkeletonItem } from '@atlaskit/side-navigation';
import { isFedRamp } from '@atlassian/atl-context';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { EngagementSpotlight } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/engagement-spotlight/main.tsx';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { SpotlightTarget } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/spotlight-target/index.tsx';
import {
	matchUrlPathname,
	matchPartialUrlPathname,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { PROJECT_SETTINGS_SHIFTER_WEIGHT, SETTINGS_IDS } from '../../../constants.tsx';
import type { SettingFeatures } from '../../../types/index.tsx';
import { getSpaUrlForClassicSettings } from '../../../utils/servicedesk-spa-url-classic-settings/index.tsx';
import { VirtualAgent } from '../../virtual-agent/index.tsx';
import { VirtualAgentMenuItem } from '../../virtual-agent/main.tsx';
import messages from './messages.tsx';

type ChannelsMenuProps = {
	projectKey: string;
	features: SettingFeatures | null;
	onHandleLegacySPAClick: (
		e:
			| SyntheticEvent
			| MouseEvent<HTMLAnchorElement>
			| KeyboardEvent<HTMLAnchorElement>
			| MouseEvent<EventTarget>
			| KeyboardEvent<EventTarget>,
	) => void;
};

export const ChannelsMenu = ({
	projectKey,
	features,
	onHandleLegacySPAClick,
}: ChannelsMenuProps) => {
	const { formatMessage } = useIntl();
	const classicSettingsUrl = `/plugins/servlet/project-config/${projectKey}`;
	const BackMenuOverrides = {
		BackMenuItem: {
			href: classicSettingsUrl,
			onClick: (ev: SyntheticEvent<Element, Event>) => ev.preventDefault(),
			title: formatMessage(messages.itemBack),
		},
	};
	if (features == null || projectKey == null) return <SkeletonItem />;

	return (
		<>
			{features.hasPortalSettings && (
				<MenuItem
					analytics={{ itemId: SETTINGS_IDS.PORTAL_SETTINGS }}
					href={getSpaUrlForClassicSettings('portal-settings', projectKey)}
					selectedOn={matchPartialUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(messages.portalSettings)}
				</MenuItem>
			)}

			<SpotlightTarget name="spotlight-nav-servicedesk/sd-project-sidebar-email-settings">
				<MenuItem
					analytics={{ itemId: SETTINGS_IDS.EMAIL_REQUESTS }}
					href={getSpaUrlForClassicSettings('email', projectKey)}
					onClick={__SPA__ ? undefined : onHandleLegacySPAClick}
					selectedOn={matchUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(messages.emailRequests)}
				</MenuItem>
				<EngagementSpotlight engagementId="spotlight-nav-servicedesk/sd-project-sidebar-email-settings" />
			</SpotlightTarget>

			{features.hasChat && (
				<MenuItem
					analytics={{
						itemId: SETTINGS_IDS.PROJECT_CHAT_ADDON,
					}}
					href={getSpaUrlForClassicSettings('chat', projectKey, '?placement=projectSettings')}
					onClick={__SPA__ ? undefined : onHandleLegacySPAClick}
					selectedOn={matchUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(messages.chat)}
				</MenuItem>
			)}
			{features.hasAddonInstaller && (
				<MenuItem
					analytics={{
						itemId: SETTINGS_IDS.PROJECT_WIDGET_ADDON_INSTALLER,
					}}
					href={getSpaUrlForClassicSettings('widget', projectKey)}
					onClick={onHandleLegacySPAClick}
					selectedOn={matchUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(messages.widget)}
				</MenuItem>
			)}
			{features.hasWidgetAddon && (
				<MenuItem
					analytics={{
						itemId: SETTINGS_IDS.PROJECT_WIDGET_ADDON,
					}}
					href={getSpaUrlForClassicSettings('widget', projectKey)}
					onClick={__SPA__ ? undefined : onHandleLegacySPAClick}
					selectedOn={matchUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(messages.widget)}
				</MenuItem>
			)}

			{features.hasWidgetAddonDev && (
				<MenuItem
					analytics={{
						itemId: SETTINGS_IDS.PROJECT_WIDGET_ADDON,
					}}
					href={getSpaUrlForClassicSettings('widget', projectKey)}
					onClick={__SPA__ ? undefined : onHandleLegacySPAClick}
					selectedOn={matchUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(messages.widget)}
				</MenuItem>
			)}

			{features.hasKnowledgeBase && (
				<MenuItem
					analytics={{
						itemId: SETTINGS_IDS.KNOWLEDGE_BASE,
					}}
					href={getSpaUrlForClassicSettings('confluence-kb', projectKey)}
					selectedOn={matchUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(messages.knowledgeBase)}
				</MenuItem>
			)}

			{(!fg('jcs_master_flag') || features.hasVirtualAgent) && !isFedRamp() && (
				<JSErrorBoundary
					id="virtualAgent"
					packageName="jiraNavigationAppsSidebarServicedesk"
					teamName="navigation"
					fallback={() => <VirtualAgentMenuItem projectKey={projectKey} />}
				>
					<VirtualAgent
						projectKey={projectKey}
						overrides={BackMenuOverrides}
						shifter={{
							groupTitle: formatMessage(messages.title),
							context: projectKey,
							weight: PROJECT_SETTINGS_SHIFTER_WEIGHT,
						}}
					/>
				</JSErrorBoundary>
			)}

			{(!fg('jcs_master_flag') || features.hasExternalLinks) && (
				<MenuItem
					analytics={{ itemId: SETTINGS_IDS.EXTERNAL_LINKS }}
					href={getSpaUrlForClassicSettings('external-links', projectKey)}
					selectedOn={matchUrlPathname()}
				>
					{formatMessage(messages.externalResources)}
				</MenuItem>
			)}

			{features.hasIssueCollectors && (
				<MenuItem
					analytics={{
						itemId: SETTINGS_IDS.ISSUE_COLLECTORS,
					}}
					ref={createRef()}
					href={`/secure/ViewCollectors!default.jspa?projectKey=${projectKey}`}
					selectedOn={(currentLocation) =>
						Boolean(currentLocation.pathname.match(/secure\/(.+)Collector/))
					}
				>
					{formatMessage(messages.issueCollectors)}
				</MenuItem>
			)}
		</>
	);
};
